/**
 * Accounts / Funds — two views of "where money sits", under one sidebar entry.
 *
 * They were two separate sidebar items. Finance kept growing (Transfers, Reconciliation, Investigations…) and
 * the section became a wall of links, so the two that answer the same question got paired: an ACCOUNT is where
 * money physically is (a bank account, a till), a FUND is what it is earmarked for. A treasurer reading one
 * almost always wants the other, and neither is long enough to earn its own top-level entry.
 *
 * Each tab keeps its own page exactly as it was — this is a navigation change, not a merge of the two screens.
 *
 * The paths are UNCHANGED (`/finance/accounts`, `/finance/funds`). Both are already linked from redirects,
 * widgets and bookmarks, and both are real URL segments, so each tab stays deep-linkable (a project rule) with
 * no redirect churn. That is why this has no parent segment of its own.
 */
export enum FinanceAccountsFundsTab {
  Accounts = "accounts",
  Funds = "funds",
}

export const FINANCE_ACCOUNTS_FUNDS_TABS: ReadonlyArray<{
  value: FinanceAccountsFundsTab
  /** Key under the `churchNav.sections.finance` namespace — the same labels the sidebar used. */
  labelKey: string
}> = [
  { value: FinanceAccountsFundsTab.Accounts, labelKey: "accounts" },
  { value: FinanceAccountsFundsTab.Funds, labelKey: "funds" },
]

export function financeAccountsFundsTabPath(tab: FinanceAccountsFundsTab): string {
  return `/finance/${tab}`
}

/** Every URL this pair owns — what the single sidebar entry highlights on. */
export const FINANCE_ACCOUNTS_FUNDS_URLS: readonly string[] = FINANCE_ACCOUNTS_FUNDS_TABS.map((tab) =>
  financeAccountsFundsTabPath(tab.value),
)

/**
 * Which tab a pathname is on.
 *
 * Matches on a trailing path SEGMENT rather than a substring: `/finance/accounts/:accountId` is an account
 * detail page that still belongs to the Accounts tab, while a bare `includes` would also fire on unrelated
 * paths that merely contain the word. Falls back to Accounts so the bar never renders with nothing selected.
 */
export function parseFinanceAccountsFundsTabFromPathname(pathname: string): FinanceAccountsFundsTab {
  const segments = pathname.split("/").filter(Boolean)
  const financeIndex = segments.lastIndexOf("finance")
  const next = financeIndex >= 0 ? segments[financeIndex + 1] : undefined
  return (
    FINANCE_ACCOUNTS_FUNDS_TABS.find((tab) => tab.value === next)?.value ?? FinanceAccountsFundsTab.Accounts
  )
}
